import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight, Check, Minus, Handshake, Building2, Dumbbell, Plane, TrainFront, ShoppingCart, Play, ShieldCheck, Sun, Droplets, Moon, Flower2, MapPin, Bug, RefreshCw, Clock, Bell, Sparkles, SprayCan, Hand, Home as HomeIcon, BarChart3, User, Signal, Wifi, BatteryMedium, ChevronRight, Brain, Thermometer, CalendarDays, X } from "lucide-react";
import { ASSETS, SAFE_BAND_URL } from "@/lib/assets";
import { Reveal } from "@/components/Section";
import { SenseRelayBanner } from "@/components/SenseRelayBanner";
import ZoomImage from "@/components/ZoomImage";
import { SEO } from "@/components/SEO";
import { PAGE_SEO, SAFE_BAND_PRODUCT_JSON_LD } from "@/lib/seoData";

const NURSE_HEALTHCARE =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/w8r7rzl6_nurse%20hlathcare.jpg";
import ProductHero from "@/components/ProductHero";

const FILL_VIDEO_POSTER =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/0m85ukii_fill%20volume%202.jpg";

const CONNECTED_CASE_BANNERS = [
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/jtwc70tz_cellphone%20banner%203%20people-2.jpg",
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/tjo3m8ww_cellphone%20%20showcase%206.jpg",
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/uq5umbyu_cellphone%20dimensions%202.jpg",
];

function ConnectedCaseBanner() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % CONNECTED_CASE_BANNERS.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);
  return (
    <div
      className="relative w-full h-[52vh] md:h-[70vh] overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 78% 72% at 50% 44%, rgba(160,26,82,0.55), rgba(70,10,38,0.5) 55%, rgba(12,4,10,1) 82%)",
      }}
      data-testid="connected-case-banner"
    >
      {/* Soft magenta bloom for depth behind the renders */}
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[85%] h-[80%] blur-3xl"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(255,45,120,0.35), rgba(255,120,60,0.12) 45%, transparent 70%)",
        }}
      />
      {CONNECTED_CASE_BANNERS.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: idx === i ? 1 : 0, transition: "opacity 1600ms ease" }}
        />
      ))}
      {/* Magenta-gold light beams streaming in like sunlight */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden mix-blend-screen">
        {[
          { left: "-8%", delay: "0s", dur: "8s" },
          { left: "28%", delay: "2.6s", dur: "9.5s" },
          { left: "62%", delay: "4.2s", dur: "7.5s" },
        ].map((r) => (
          <div
            key={r.left}
            className="absolute top-[-30%] h-[160%] w-[26%]"
            style={{ left: r.left, transform: "rotate(18deg)" }}
          >
            <div
              className="w-full h-full"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(255,45,120,0.4), rgba(255,180,90,0.28), transparent)",
                filter: "blur(30px)",
                animation: `lightStream ${r.dur} ease-in-out ${r.delay} infinite`,
              }}
            />
          </div>
        ))}
      </div>
      {/* legibility scrims blending into the dark section below */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/85 via-transparent to-[#0a0a0a]/40" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0a0a0a] to-transparent" />

      {/* progress dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5">
        {CONNECTED_CASE_BANNERS.map((src, i) => (
          <span
            key={src}
            className="h-1.5 rounded-full transition-all duration-500"
            style={{
              width: idx === i ? 28 : 8,
              backgroundColor: idx === i ? "#ff2d78" : "rgba(255,255,255,0.4)",
            }}
          />
        ))}
      </div>
    </div>
  );
}


const PHONE_APPS = [
  {
    id: "sunscreen",
    name: "Sunscreen",
    chipIcon: Sun,
    title: "SUNCARE",
    subtitle: "Stay protected. Stay radiant.",
    formula: "SPF 50 Sunscreen",
    grad: { from: "#ff2d78", to: "#5c0a2c" },
    accent: "#ffcf5a",
    action: "I Applied It",
    actionDone: "Applied",
    secondary: "Remind me later",
    eventLabel: "Sunscreen applied",
    reminderMin: 120,
    seedAgoMin: 42,
    seedCount: 3,
    statusLabel: "PROTECTION STATUS",
    idleHint: "Apply now for UV protection",
    cards: [{ icon: Sun, label: "UV INDEX", value: "6 · High", sub: "Strong UV exposure" }],
  },
  {
    id: "moisturizer",
    name: "Moisturizer",
    chipIcon: Droplets,
    title: "HYDRATE",
    subtitle: "Healthy skin, all day.",
    formula: "Daily Moisturizer",
    grad: { from: "#17b3a6", to: "#083f3c" },
    accent: "#b8f5ee",
    action: "I Applied It",
    actionDone: "Applied",
    secondary: "Remind me later",
    eventLabel: "Moisturizer applied",
    reminderMin: 240,
    seedAgoMin: 240,
    seedCount: 2,
    statusLabel: "HYDRATION STATUS",
    idleHint: "Skin refresh may be useful",
    nextText: "Next routine tonight at 8:00 PM",
    cards: [{ icon: Droplets, label: "ENVIRONMENT", value: "Humidity 34%", sub: "Dry conditions" }],
  },
  {
    id: "serum",
    name: "Serum",
    chipIcon: Sparkles,
    title: "RITUAL",
    subtitle: "Your nightly reset.",
    formula: "Night Serum",
    grad: { from: "#7b5bff", to: "#241653" },
    accent: "#d9ccff",
    action: "Begin Ritual",
    actionDone: "Serum applied",
    eventLabel: "Serum applied",
    reminderMin: 1440,
    seedAgoMin: null,
    seedCount: 11,
    seedStreak: 12,
    isRitual: true,
    statusLabel: "STREAK",
    cards: [
      { icon: Moon, label: "TONIGHT'S RITUAL", value: "Night", sub: "A calm, restorative window" },
      { icon: Clock, label: "BEST WINDOW", value: "9:30 – 10:30 PM", sub: "Best absorbed before bed" },
    ],
    steps: ["Cleanse", "Apply serum", "Moisturize"],
  },
  {
    id: "fragrance",
    name: "Fragrance",
    chipIcon: Flower2,
    title: "SCENT",
    subtitle: "Fresh, whenever.",
    formula: "Fragrance Mist",
    grad: { from: "#ff5c8a", to: "#5a0e2a" },
    accent: "#ffd0dc",
    action: "I Refreshed",
    actionDone: "Refreshed",
    secondary: "Remind me",
    eventLabel: "Fragrance refreshed",
    reminderMin: 180,
    seedAgoMin: 180,
    seedCount: 1,
    statusLabel: "LAST REFRESH",
    isScent: true,
    nextText: "Next moment · Dinner 6:30 PM",
    cards: [
      { icon: CalendarDays, label: "NEXT MOMENT", value: "Dinner · 6:30 PM", sub: "From calendar" },
      { icon: Sparkles, label: "SUGGESTED REFRESH", value: "5:55 PM", sub: "Before you leave" },
    ],
    intensities: ["Light", "Moderate", "Statement"],
  },
  {
    id: "sanitizer",
    name: "Sanitizer",
    chipIcon: SprayCan,
    title: "CLEAN",
    subtitle: "Hygiene on the move.",
    formula: "Hand Sanitizer",
    grad: { from: "#2f9bff", to: "#0a2f5c" },
    accent: "#bfe3ff",
    action: "Sanitized",
    actionDone: "Sanitized",
    eventLabel: "Sanitized",
    reminderMin: 20,
    seedAgoMin: 2,
    seedCount: 7,
    statusLabel: "TODAY",
    isClean: true,
    hygieneModes: ["Clinic", "Travel", "Gym", "School", "Work", "Custom"],
    cards: [],
  },
  {
    id: "repellent",
    name: "Repellent",
    chipIcon: Bug,
    title: "DEFEND",
    subtitle: "Ahead of the bite.",
    formula: "Insect Repellent",
    grad: { from: "#4caf50", to: "#123d1a" },
    accent: "#d6f5c9",
    action: "I Applied It",
    actionDone: "Protection refreshed",
    secondary: "Remind me in 15 min",
    eventLabel: "Repellent applied",
    reminderMin: 120,
    seedAgoMin: 95,
    seedCount: 4,
    statusLabel: "PROTECTION STATUS",
    idleHint: "Apply before dusk",
    cards: [
      { icon: Thermometer, label: "CURRENT CONDITIONS", value: "Warm · Humid", sub: "Dusk approaching" },
      { icon: Bug, label: "MOSQUITO CONDITIONS", value: "Elevated", sub: "Becoming favorable for activity" },
    ],
  },
];

const CLEAN_ROUTINE = {
  Clinic: "Between patients",
  Travel: "Every 45 min",
  Gym: "After each station",
  School: "Every hour",
  Work: "Every 30 min",
  Custom: "Custom schedule",
};

const REMINDER_ROWS = [
  { label: "Sunscreen reapplication", time: "Next · in 1 hr 20 min" },
  { label: "Evening skincare", time: "Next · 8:00 PM" },
  { label: "Fragrance before an event", time: "Next · 5:55 PM" },
  { label: "Repellent near dusk", time: "Next · 6:45 PM" },
  { label: "Sanitizer routine", time: "Repeats · every 20 min" },
  { label: "Moisturizer routine", time: "Next · 10:00 PM" },
];

function AppMockup() {
  const [sel, setSel] = useState(0);
  const [tab, setTab] = useState("home");
  const [showModes, setShowModes] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [hygiene, setHygiene] = useState("Clinic");
  const [intensity, setIntensity] = useState("Moderate");
  const [reminders, setReminders] = useState([true, true, false, true, false, false]);
  const [applied, setApplied] = useState(() => {
    const now = Date.now();
    const init = {};
    PHONE_APPS.forEach((a) => {
      init[a.id] = {
        time: a.seedAgoMin != null ? now - a.seedAgoMin * 60000 : null,
        count: a.seedCount || 0,
        streak: a.seedStreak || 0,
      };
    });
    return init;
  });
  const [events, setEvents] = useState(() => {
    const mk = (h, m, id, label) => {
      const d = new Date();
      d.setHours(h, m, 0, 0);
      return { t: d.getTime(), id, label };
    };
    return [
      mk(14, 17, "sunscreen", "Sunscreen applied"),
      mk(12, 4, "sunscreen", "Sunscreen applied"),
      mk(8, 10, "sunscreen", "Sunscreen applied"),
      mk(18, 12, "repellent", "Repellent applied"),
    ];
  });

  const p = PHONE_APPS[sel];
  const st = applied[p.id] || { time: null, count: 0, streak: 0 };
  const touch = () => setInteracted(true);

  useEffect(() => {
    if (interacted) return undefined;
    const id = setInterval(() => setSel((s) => (s + 1) % PHONE_APPS.length), 3200);
    return () => clearInterval(id);
  }, [interacted]);

  const fmt = (ms) => new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const ago = (ms) => {
    if (ms == null) return null;
    const m = Math.max(0, Math.round((Date.now() - ms) / 60000));
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return mm ? `${h} hr ${mm} min ago` : `${h} hr ago`;
  };
  const nextLine = () => {
    if (p.isRitual) return `${st.streak || 0} night streak`;
    if (p.nextText) return p.nextText;
    if (st.time) return `Next reminder at ${fmt(st.time + p.reminderMin * 60000)}`;
    return "";
  };

  const handleApply = () => {
    touch();
    const now = Date.now();
    setApplied((prev) => ({
      ...prev,
      [p.id]: {
        time: now,
        count: (prev[p.id]?.count || 0) + 1,
        streak: (prev[p.id]?.streak || 0) + (p.isRitual ? 1 : 0),
      },
    }));
    setEvents((prev) => [{ t: now, id: p.id, label: p.eventLabel }, ...prev]);
    setConfirm(true);
    setTimeout(() => setConfirm(false), 2200);
  };

  const selectFormula = (i) => {
    touch();
    setSel(i);
    setShowModes(false);
    setTab("home");
    setConfirm(false);
  };

  const glowTile = (Ic) => (
    <div
      className="rounded-xl p-2 shrink-0"
      style={{ background: `linear-gradient(135deg, ${p.accent}59, ${p.accent}1f)`, boxShadow: `0 0 10px ${p.accent}33` }}
    >
      <Ic className="w-4 h-4" style={{ color: p.accent }} />
    </div>
  );

  const infoCard = (c) => (
    <div key={c.label} className="rounded-2xl bg-white/10 border border-white/10 p-3 flex items-center gap-3">
      {glowTile(c.icon)}
      <div className="min-w-0">
        <span className="block text-[9px] tracking-[0.18em] text-white/70">{c.label}</span>
        <span className="block text-base font-bold leading-tight">{c.value}</span>
        {c.sub ? <span className="block text-[9px] text-white/70">{c.sub}</span> : null}
      </div>
    </div>
  );

  const nav = [
    { id: "home", Ic: HomeIcon, l: "Home" },
    { id: "history", Ic: BarChart3, l: "History" },
    { id: "reminders", Ic: Bell, l: "Reminders" },
    { id: "profile", Ic: User, l: "Profile" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mt-12" data-testid="connected-case-app-mockup">
      {/* Phone */}
      <div className="flex justify-center order-2 lg:order-1">
        <div className="relative w-[300px] max-w-full">
          <div
            className="pointer-events-none absolute -inset-8 blur-3xl opacity-50"
            style={{ background: `radial-gradient(circle, ${p.grad.from}55, transparent 68%)` }}
          />
          <div
            className="relative rounded-[2.75rem] border-[10px] border-zinc-900 bg-black shadow-2xl overflow-hidden"
            style={{ aspectRatio: "9 / 19" }}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-b-2xl z-20" />
            <div
              key={p.id}
              className="absolute inset-0 flex flex-col text-white"
              style={{
                background: `radial-gradient(125% 65% at 50% 0%, ${p.accent}4d, transparent 55%), linear-gradient(165deg, ${p.grad.from}, ${p.grad.to})`,
                animation: "fadeInApp 480ms ease",
              }}
            >
              {/* status bar */}
              <div className="flex items-center justify-between px-6 pt-3.5 text-[11px] font-semibold shrink-0">
                <span>9:41</span>
                <div className="flex items-center gap-1.5">
                  <Signal className="w-3.5 h-3.5" />
                  <Wifi className="w-3.5 h-3.5" />
                  <BatteryMedium className="w-4 h-4" />
                </div>
              </div>

              {/* scrollable content */}
              <div className="flex-1 overflow-y-auto hide-scroll px-5 pb-3">
                {tab === "home" && (
                  <div>
                    <div className="mt-2">
                      <h4 className="font-heading text-2xl font-semibold tracking-[0.12em]">{p.title}</h4>
                      <p className="text-[11px] text-white/80 mt-0.5">{p.subtitle}</p>
                    </div>

                    {/* current formula (interactive) */}
                    <div className="mt-4 rounded-2xl bg-white/15 border border-white/20 backdrop-blur-sm p-3">
                      <div className="flex items-center gap-3">
                        {glowTile(ShieldCheck)}
                        <div className="min-w-0 flex-1">
                          <span className="block text-[9px] tracking-[0.18em] text-white/70">CURRENT FORMULA</span>
                          <span className="block text-sm font-semibold leading-tight">{p.formula}</span>
                          <span className="block text-[9px] text-white/70">Selected by user</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => { touch(); setShowModes(true); }}
                          data-testid="app-change-formula"
                          className="text-[10px] font-semibold px-2.5 py-1 rounded-full border border-white/30 hover:bg-white/10 transition-colors shrink-0"
                        >
                          Change
                        </button>
                      </div>
                      <div className="mt-2 pt-2 border-t border-white/10 text-[8px] tracking-[0.18em] text-white/50">
                        RESERVOIR SETUP: USER SELECTED
                      </div>
                    </div>

                    {/* contextual info cards (SUNCARE gets a UV gauge dial) */}
                    {p.id === "sunscreen" ? (
                      <div className="mt-3 rounded-2xl bg-white/10 border border-white/10 p-4 flex items-center gap-4">
                        <div className="relative w-[76px] h-[76px] shrink-0">
                          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                            <defs>
                              <linearGradient id="uvGaugeGrad" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#7CFC63" />
                                <stop offset="55%" stopColor="#ffcf5a" />
                                <stop offset="100%" stopColor="#ff5c3a" />
                              </linearGradient>
                            </defs>
                            <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="9" />
                            <circle
                              cx="50"
                              cy="50"
                              r="42"
                              fill="none"
                              stroke="url(#uvGaugeGrad)"
                              strokeWidth="9"
                              strokeLinecap="round"
                              strokeDasharray={2 * Math.PI * 42}
                              strokeDashoffset={2 * Math.PI * 42 * (1 - 6 / 11)}
                            />
                          </svg>
                          <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className="text-2xl font-bold leading-none">6</span>
                            <span className="text-[8px] tracking-[0.2em] text-white/70 mt-0.5">HIGH</span>
                          </div>
                        </div>
                        <div className="min-w-0">
                          <span className="block text-[9px] tracking-[0.18em] text-white/70">UV INDEX</span>
                          <span className="block text-sm font-semibold leading-tight">Strong UV exposure</span>
                          <span className="block text-[9px] text-white/70 mt-0.5">Seek shade 10 AM – 4 PM</span>
                        </div>
                      </div>
                    ) : (
                      p.cards.length > 0 && <div className="mt-3 space-y-2.5">{p.cards.map(infoCard)}</div>
                    )}

                    {/* CLEAN, hygiene mode */}
                    {p.isClean && (
                      <div className="mt-2.5 rounded-2xl bg-white/10 border border-white/10 p-3">
                        <span className="block text-[9px] tracking-[0.18em] text-white/70 mb-2">HYGIENE MODE</span>
                        <div className="flex flex-wrap gap-1.5">
                          {p.hygieneModes.map((m) => {
                            const on = hygiene === m;
                            return (
                              <button
                                key={m}
                                type="button"
                                onClick={() => { touch(); setHygiene(m); }}
                                data-testid={`app-clean-mode-${m}`}
                                className="text-[10px] px-2.5 py-1 rounded-full border transition-colors"
                                style={on ? { background: p.accent, color: "#0a0a0a", borderColor: p.accent } : { borderColor: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.8)" }}
                              >
                                {m}
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-2.5 flex items-center justify-between text-[10px]">
                          <span className="text-white/60">ROUTINE</span>
                          <span className="font-semibold">{CLEAN_ROUTINE[hygiene]}</span>
                        </div>
                      </div>
                    )}

                    {/* SCENT, intensity preference */}
                    {p.isScent && (
                      <div className="mt-2.5 rounded-2xl bg-white/10 border border-white/10 p-3">
                        <span className="block text-[9px] tracking-[0.18em] text-white/70 mb-2">SCENT INTENSITY</span>
                        <div className="flex gap-1.5">
                          {p.intensities.map((x) => {
                            const on = intensity === x;
                            return (
                              <button
                                key={x}
                                type="button"
                                onClick={() => { touch(); setIntensity(x); }}
                                data-testid={`app-scent-intensity-${x}`}
                                className="flex-1 text-[10px] px-2 py-1.5 rounded-full border transition-colors"
                                style={on ? { background: p.accent, color: "#0a0a0a", borderColor: p.accent } : { borderColor: "rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.8)" }}
                              >
                                {x}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* RITUAL, steps */}
                    {p.isRitual && (
                      <div className="mt-2.5 rounded-2xl bg-white/10 border border-white/10 p-3">
                        <span className="block text-[9px] tracking-[0.18em] text-white/70 mb-2">TONIGHT'S STEPS</span>
                        <div className="space-y-1.5">
                          {p.steps.map((s, i) => (
                            <div key={s} className="flex items-center gap-2 text-[11px]">
                              <span
                                className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold shrink-0"
                                style={{ background: i === 1 ? p.accent : "rgba(255,255,255,0.15)", color: i === 1 ? "#0a0a0a" : "#fff" }}
                              >
                                {i + 1}
                              </span>
                              <span className={i === 1 ? "font-semibold" : "text-white/70"}>{s}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* status card */}
                    <div className="mt-2.5 rounded-2xl bg-white/10 border border-white/10 p-3">
                      <span className="block text-[9px] tracking-[0.18em] text-white/70">{p.statusLabel}</span>
                      {p.isRitual ? (
                        <>
                          <span className="block text-base font-bold leading-tight">{st.streak} night streak</span>
                          <span className="block text-[9px] text-white/70">{st.time ? `Last ritual ${ago(st.time)}` : "Begin tonight's ritual"}</span>
                        </>
                      ) : p.isClean ? (
                        <>
                          <span className="block text-base font-bold leading-tight">Applications today: {st.count}</span>
                          <span className="block text-[9px] text-white/70">{st.time ? `Last sanitized ${ago(st.time)}` : "Tap Sanitized to start"}</span>
                        </>
                      ) : st.time ? (
                        <>
                          <span className="block text-base font-bold leading-tight">Applied {ago(st.time)}</span>
                          <span className="block text-[9px] text-white/70">{nextLine()}</span>
                        </>
                      ) : (
                        <>
                          <span className="block text-base font-bold leading-tight">Not applied yet</span>
                          <span className="block text-[9px] text-white/70">{p.idleHint}</span>
                        </>
                      )}
                    </div>

                    {/* confirmation banner */}
                    {confirm && (
                      <div
                        className="mt-2.5 rounded-2xl border p-2.5 flex items-center gap-2.5"
                        style={{ borderColor: `${p.accent}66`, background: `${p.accent}22`, animation: "checkPop 340ms ease" }}
                      >
                        <span className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: p.accent }}>
                          <Check className="w-3.5 h-3.5 text-black" />
                        </span>
                        <div className="min-w-0">
                          <span className="block text-[11px] font-semibold">{p.actionDone} at {st.time ? fmt(st.time) : fmt(Date.now())}</span>
                          <span className="block text-[9px] text-white/80">{nextLine()}</span>
                        </div>
                      </div>
                    )}

                    {/* primary action */}
                    <button
                      type="button"
                      onClick={handleApply}
                      data-testid="app-primary-action"
                      className="mt-3 w-full rounded-2xl py-3 font-semibold text-sm text-black transition-transform active:scale-[0.98]"
                      style={{ background: p.accent, boxShadow: confirm ? `0 0 22px ${p.accent}aa` : "none" }}
                    >
                      {p.action}
                    </button>
                    {p.secondary && (
                      <button
                        type="button"
                        onClick={touch}
                        data-testid="app-secondary-action"
                        className="mt-2 w-full text-center text-[11px] text-white/70 hover:text-white transition-colors"
                      >
                        {p.secondary}
                      </button>
                    )}
                  </div>
                )}

                {tab === "history" && (
                  <div className="mt-2">
                    <h4 className="font-heading text-2xl font-semibold tracking-[0.12em]">HISTORY</h4>
                    <p className="text-[11px] text-white/80 mt-0.5 mb-3">Your confirmed applications</p>
                    <div className="space-y-2">
                      {events.map((e, i) => {
                        const app = PHONE_APPS.find((a) => a.id === e.id);
                        return (
                          <div key={`${e.t}-${i}`} className="rounded-2xl bg-white/10 border border-white/10 p-3 flex items-center gap-3">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: app ? app.accent : "#fff" }} />
                            <div className="flex-1 min-w-0">
                              <span className="block text-[12px] font-semibold">{e.label}</span>
                              <span className="block text-[9px] text-white/60">User confirmed</span>
                            </div>
                            <span className="text-[11px] text-white/80 shrink-0">{fmt(e.t)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {tab === "reminders" && (
                  <div className="mt-2">
                    <h4 className="font-heading text-2xl font-semibold tracking-[0.12em]">REMINDERS</h4>
                    <p className="text-[11px] text-white/80 mt-0.5 mb-3">Contextual to your formula</p>
                    <div className="space-y-2">
                      {REMINDER_ROWS.map((r, i) => (
                        <button
                          key={r.label}
                          type="button"
                          onClick={() => { touch(); setReminders((prev) => prev.map((v, j) => (j === i ? !v : v))); }}
                          data-testid={`app-reminder-toggle-${i}`}
                          className="w-full rounded-2xl bg-white/10 border border-white/10 p-3 flex items-center justify-between gap-3"
                        >
                          <span className="text-left min-w-0">
                            <span className="block text-[12px] font-medium truncate">{r.label}</span>
                            <span className="block text-[9px] mt-0.5" style={{ color: reminders[i] ? p.accent : "rgba(255,255,255,0.4)" }}>{reminders[i] ? r.time : "Off"}</span>
                          </span>
                          <span className="w-9 h-5 rounded-full p-0.5 transition-colors shrink-0" style={{ background: reminders[i] ? p.accent : "rgba(255,255,255,0.2)" }}>
                            <span className="block w-4 h-4 rounded-full bg-white transition-transform" style={{ transform: reminders[i] ? "translateX(16px)" : "translateX(0)" }} />
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {tab === "profile" && (
                  <div className="mt-2">
                    <h4 className="font-heading text-2xl font-semibold tracking-[0.12em]">PROFILE</h4>
                    <div className="mt-4 flex flex-col items-center">
                      <span className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${p.accent}, ${p.grad.from})` }}>
                        <User className="w-7 h-7 text-black" />
                      </span>
                      <span className="mt-2 text-sm font-semibold">WearSpray</span>
                    </div>
                    <div className="mt-4 space-y-2">
                      <div className="rounded-2xl bg-white/10 border border-white/10 p-3 flex items-center justify-between text-[11px]"><span className="text-white/60">Reservoir setup</span><span className="font-semibold">User selected</span></div>
                      <div className="rounded-2xl bg-white/10 border border-white/10 p-3 flex items-center justify-between text-[11px]"><span className="text-white/60">Current formula</span><span className="font-semibold">{p.formula}</span></div>
                      <div className="rounded-2xl bg-white/10 border border-white/10 p-3 flex items-center justify-between text-[11px]"><span className="text-white/60">Confirmations today</span><span className="font-semibold">{st.count}</span></div>
                    </div>
                  </div>
                )}
              </div>

              {/* bottom nav */}
              <div className="bg-black/30 px-5 py-3 flex items-center justify-between shrink-0 border-t border-white/10">
                {nav.map((t) => {
                  const on = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => { touch(); setTab(t.id); }}
                      data-testid={`app-tab-${t.id}`}
                      className="flex flex-col items-center gap-1 transition-opacity"
                      style={{ opacity: on ? 1 : 0.5 }}
                    >
                      <t.Ic className="w-4 h-4" style={on ? { color: p.accent } : undefined} />
                      <span className="text-[8px] tracking-wide">{t.l}</span>
                    </button>
                  );
                })}
              </div>

              {/* mode-select overlay */}
              {showModes && (
                <div
                  data-testid="app-mode-select"
                  className="absolute inset-0 z-30 flex flex-col p-5"
                  style={{ background: "linear-gradient(165deg, #14060d, #05040a)", animation: "fadeInApp 320ms ease" }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-heading text-xl font-semibold leading-tight">WHAT ARE YOU CARRYING TODAY?</h4>
                      <p className="text-[10px] text-white/60 mt-1.5">Choose what you have placed in your WearSpray reservoir.</p>
                    </div>
                    <button type="button" onClick={() => setShowModes(false)} data-testid="app-mode-close" className="text-white/60 hover:text-white shrink-0 ml-2">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4 overflow-y-auto hide-scroll">
                    {PHONE_APPS.map((a, i) => {
                      const Ic = a.chipIcon;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => selectFormula(i)}
                          data-testid={`app-mode-option-${a.id}`}
                          className="rounded-2xl border border-white/15 p-3 flex flex-col gap-2 hover:bg-white/10 transition-colors text-left"
                          style={{ background: `linear-gradient(150deg, ${a.grad.from}40, transparent)` }}
                        >
                          <span className="rounded-lg p-1.5 w-fit" style={{ background: `${a.accent}33` }}>
                            <Ic className="w-4 h-4" style={{ color: a.accent }} />
                          </span>
                          <span className="text-[11px] font-semibold leading-tight">{a.name}</span>
                          <span className="text-[8px] text-white/50">{a.formula}</span>
                        </button>
                      );
                    })}
                    <div className="rounded-2xl border border-dashed border-white/15 p-3 flex flex-col gap-2 justify-center">
                      <span className="text-[11px] font-semibold">Custom</span>
                      <span className="text-[8px] text-white/50">Name your own formula</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Selector + copy */}
      <div className="order-1 lg:order-2">
        <span className="font-mono text-xs tracking-[0.24em] uppercase text-accent block mb-4">Tap a Formula</span>
        <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug text-white max-w-md">
          The same app, reshaped by what you decide to carry.
        </h3>
        <p className="font-body text-zinc-400 text-sm leading-relaxed mt-4 max-w-md">
          You tell the app what you loaded, then confirm each time you apply.
          Tap a formula here, or tap <span className="text-white">Change</span> on the phone.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mt-7">
          {PHONE_APPS.map((a, i) => {
            const Ic = a.chipIcon;
            const active = sel === i;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => selectFormula(i)}
                aria-pressed={active}
                data-testid={`app-formula-chip-${a.id}`}
                className="flex items-center gap-2 font-mono text-xs tracking-wide px-3 py-2.5 border rounded-full transition-colors duration-200"
                style={
                  active
                    ? { color: "#0a0a0a", backgroundColor: "#ff2d78", borderColor: "#ff2d78" }
                    : { color: "#d4d4d8", borderColor: "#3f3f46", backgroundColor: "transparent" }
                }
              >
                <Ic className="w-3.5 h-3.5 shrink-0" />
                {a.name}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


function FillDemoVideo() {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="relative mt-12 max-w-4xl" data-testid="products-fill-demo-wrap">
      <div
        className="absolute -inset-6 blur-3xl opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.16), transparent 65%)" }}
      />
      <div className="relative border border-zinc-800 bg-black overflow-hidden shadow-2xl shadow-cyan-500/10">
        <div className="aspect-video w-full">
          {playing ? (
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/uophI7vAoNA?autoplay=1&rel=0"
              title="How to fill the WearSpray reservoir"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              data-testid="products-fill-video"
            />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label="Play the reservoir fill demo video"
              data-testid="products-fill-poster"
              className="group relative w-full h-full block cursor-pointer"
            >
              <img loading="lazy"
                src={FILL_VIDEO_POSTER}
                alt="Pouring a formulation into the open WearSpray reservoir"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              {/* Scrims for depth + legibility */}
              <div className="absolute inset-0 bg-black/25 transition-colors duration-300 group-hover:bg-black/15" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-black/30" />

              {/* Play button */}
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="relative flex items-center justify-center">
                  <span className="absolute w-24 h-24 rounded-full bg-accent/20 blur-xl transition-opacity duration-300 group-hover:opacity-90" />
                  <span className="relative flex items-center justify-center w-[74px] h-[74px] rounded-full bg-white/95 shadow-2xl transition-transform duration-300 group-hover:scale-110">
                    <Play size={26} strokeWidth={2} className="text-black translate-x-[2px]" fill="currentColor" />
                  </span>
                </span>
              </span>

              {/* Caption card */}
              <span className="absolute left-0 bottom-0 p-6 md:p-8 text-left">
                <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-accent block mb-3">
                  Watch · 60 sec
                </span>
                <span className="block font-heading text-xl md:text-2xl font-light tracking-tight text-white leading-snug max-w-md">
                  Fill it, wear it, dispense it, in seconds.
                </span>
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const FEATURES = [
  "Refillable and reusable",
  "Wrist-worn",
  "Palm-directed dispensing",
  "Compatible with selected liquids, lotions, gels, serums, and mists",
  "Manufactured and physically available today",
];

const FORMATS = [
  { label: "Silicone Format", tags: ["Lifestyle", "Beauty", "Personal care", "Everyday use"] },
  { label: "Adjustable Velcro Format", tags: ["Sports", "Outdoor use", "Healthcare", "Institutional applications", "Custom fitting"] },
];

const COMPARISON = [
  "Always with the user",
  "Immediate access",
  "Refillable and reusable",
  "Controlled dispensing",
  "Works during motion and activity",
];

const APPLICATIONS = [
  { icon: Sun, t: "Sun Care", d: "Sunscreen and compatible sun protection formulations." },
  { icon: Bug, t: "Insect Protection", d: "Repellent and outdoor protection formulations." },
  { icon: SprayCan, t: "Hygiene", d: "Sanitizer and compatible personal hygiene formulations." },
  { icon: Droplets, t: "Skincare", d: "Moisturizers, serums, lotions, and soothing topical care." },
  { icon: Flower2, t: "Fragrance", d: "Perfume, body mist, and personal fragrance." },
  { icon: Dumbbell, t: "Active & Professional Use", d: "Sports, travel, outdoor activity, healthcare, and other situations where immediate access matters." },
];

const PROGRESSION = ["Carry", "Sense", "Understand", "Prompt", "Act", "Learn"];

const SENSE_ACT = [
  { t: "Sense", d: "Capture relevant biometric information.", c: "#00e5ff", Icon: Signal },
  { t: "Understand", d: "Connected technology interprets the information and context.", c: "#3ad6a0", Icon: Sparkles },
  { t: "Prompt", d: "Provide a useful reminder, alert, or recommendation.", c: "#e6c068", Icon: Bell },
  { t: "Act", d: "The WearSpray dispenser keeps the appropriate topical solution immediately available.", c: "#ff2d78", Icon: Hand },
  { t: "Learn", d: "Each check-in teaches WearSpray your routine, so its guidance keeps getting more personal over time.", c: "#b794f6", Icon: Brain },
];

function SenseActFlow() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % (SENSE_ACT.length + 1)), 900);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px mt-16 bg-zinc-800 border border-zinc-800" data-testid="products-sense-act-flow">
      {SENSE_ACT.map((s, i) => {
        const on = i === active;
        const Icon = s.Icon;
        return (
          <div key={s.t} data-testid={`products-sense-act-${i}`} className="relative p-8 h-full overflow-hidden transition-colors duration-700" style={{ backgroundColor: on ? "#161616" : "#111" }}>
            <div className="absolute inset-x-0 top-0 h-[3px] transition-opacity duration-700" style={{ backgroundColor: s.c, opacity: on ? 1 : 0.15 }} />
            <div className="absolute -inset-10 blur-3xl pointer-events-none transition-opacity duration-700" style={{ background: `radial-gradient(circle at 50% 0%, ${s.c}44, transparent 60%)`, opacity: on ? 0.7 : 0 }} />
            <div className="relative flex items-center justify-between">
              <div className="relative w-9 h-9 flex items-center justify-center">
                {on && <span className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: `${s.c}33` }} />}
                <Icon size={22} strokeWidth={1.75} className="relative transition-all duration-500" style={{ color: on ? s.c : "#52525b", transform: on ? "scale(1.15)" : "scale(1)" }} />
              </div>
              <span className="font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-500" style={{ color: on ? s.c : "#52525b" }}>0{i + 1}</span>
            </div>
            <h3 className="relative font-heading text-xl mt-4 tracking-tight transition-colors duration-500" style={{ color: on ? s.c : "#ffffff" }}>{s.t}</h3>
            <p className="relative font-body text-sm leading-relaxed mt-3 transition-colors duration-500" style={{ color: on ? "#e4e4e7" : "#a1a1aa" }}>{s.d}</p>
          </div>
        );
      })}
    </div>
  );
}

const ROADMAP = [
  { n: "01", t: "WearSpray Today", d: "Refillable personal care and protection directly from the wrist." },
  { n: "02", t: "Sense + Act", d: "Body sensing combined with immediate physical access." },
  { n: "03", t: "WearSpray Mobile", d: "Dispensing and contextual intelligence extending to the smartphone." },
  { n: "04", t: "Intelligent Actionable Wearables", d: "The convergence of sensing, contextual intelligence, prompting, and physical delivery." },
];

const SENSOR_SIDE =
  "https://static.prod-images.emergentagent.com/jobs/ec62b2d0-2ad9-4890-99e3-2e35ee4d6bac/images/704592cbdeb743d3f4a00a231d0ce2b9e7715c2213a854104514f282c7b63e8b.jpeg";
const SENSOR_BOTTOM =
  "https://static.prod-images.emergentagent.com/jobs/ec62b2d0-2ad9-4890-99e3-2e35ee4d6bac/images/f0e0bc2687c26ded7819195e808ec58990e40aebb27b871878033d2197d48c10.jpeg";
const SENSOR_TOP =
  "https://static.prod-images.emergentagent.com/jobs/ec62b2d0-2ad9-4890-99e3-2e35ee4d6bac/images/b93588f67d6cb0af18acdc10d66fa282cde5f173b68e4ec58f9b68a30aff35a1.jpeg";

const ASSEMBLY_SLIDES = [
  { src: `/sensor/exploded-1.jpg` },
  { src: `/sensor/exploded-2.jpg` },
  { src: `/sensor/exploded-3.jpg` },
  { src: `/sensor/exploded-4.jpg` },
  { src: `/sensor/exploded-5.jpg` },
];

const MOBILE_STEPS = [
  { t: "Fill", d: "User loads a compatible formulation." },
  { t: "Select", d: "User tells the application what is inside." },
  { t: "Context", d: "The phone uses relevant information such as time, location, environmental conditions, and user-selected preferences." },
  { t: "Prompt", d: "The application provides relevant guidance or reminders." },
  { t: "Apply", d: "The product is already within reach." },
];

const REL_FLOW = [
  { t: "Sense", c: "#00e5ff" },
  { t: "Contextualize", c: "#3ad6a0" },
  { t: "Prompt", c: "#e6c068" },
  { t: "Apply", c: "#ff2d78" },
];

function RelevanceFlow() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % (REL_FLOW.length + 1)), 900);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 mt-8" data-testid="relevance-flow">
      {REL_FLOW.map((s, i, arr) => {
        const on = i === active;
        return (
          <div key={s.t} className="flex items-center gap-3">
            <span
              className="font-mono text-xs tracking-[0.2em] uppercase px-4 py-2.5 border transition-all duration-500"
              style={{ color: on ? s.c : "#ffffff", borderColor: on ? s.c : "#3f3f46", boxShadow: on ? `0 0 18px ${s.c}55` : "none" }}
            >
              {s.t}
            </span>
            {i < arr.length - 1 && <ArrowUpRight size={14} className="rotate-45" style={{ color: s.c }} />}
          </div>
        );
      })}
    </div>
  );
}

function AssemblyCrossfade() {
  const [i, setI] = useState(0);
  useEffect(() => {
    ASSEMBLY_SLIDES.forEach((s) => { const im = new Image(); im.src = s.src; });
    let dir = 1;
    let idx = 0;
    const id = setInterval(() => {
      if (idx >= ASSEMBLY_SLIDES.length - 1) dir = -1;
      else if (idx <= 0) dir = 1;
      idx += dir;
      setI(idx);
    }, 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="relative border border-zinc-800 overflow-hidden bg-black mt-4" data-testid="products-assembly-crossfade">
      <div className="relative w-full" style={{ aspectRatio: "1000 / 750" }}>
        {ASSEMBLY_SLIDES.map((s, idx) => (
          <img loading="lazy"
            key={s.src}
            src={s.src}
            alt="The WearSpray reservoir separating from the sensor band"
            className="absolute inset-0 w-full h-full object-contain transition-opacity duration-[1800ms] ease-in-out"
            style={{ opacity: idx === i ? 1 : 0, willChange: "opacity" }}
            data-testid={`products-assembly-slide-${idx}`}
          />
        ))}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(circle at 44% 46%, rgba(236,0,140,0.55), rgba(236,0,140,0.12) 38%, transparent 62%)", mixBlendMode: "screen" }}
        />
      </div>
      <span className="block font-mono text-[10px] tracking-[0.18em] uppercase px-3 py-2.5 text-zinc-400 border-t border-zinc-800 bg-[#0a0a0a]">
        Assembly · Bezel · Reservoir · Sensor · Band
      </span>
    </div>
  );
}


export default function Products() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash === "#how-it-works") {
      const t = setTimeout(() => {
        const el = document.getElementById("how-it-works");
        if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 40, behavior: "smooth" });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [location]);
  return (
    <div data-testid="products-page">
      <SEO {...PAGE_SEO["/products"]} path="/products" jsonLd={SAFE_BAND_PRODUCT_JSON_LD} />
      <ProductHero />

      {/* PRODUCT MAIN, silicone product shot + text */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 md:grid-cols-2 gap-16 items-stretch">
        <Reveal className="h-full">
          <div className="relative border border-zinc-800 bg-[#0d0d0d] h-full flex items-center justify-center min-h-[460px] overflow-hidden">
            <div className="absolute inset-0 radial-glow" />
            <img loading="lazy"
              src="/og/wearspray-social-preview-v2.jpg"
              alt="Safe Band Pro silicone version refillable wrist-worn dispensing device"
              className="relative w-full h-full object-cover"
            />
            <span
              data-testid="silicone-version-label"
              className="absolute bottom-4 left-4 font-mono text-[11px] tracking-[0.22em] uppercase text-white/85 bg-black/45 backdrop-blur-sm px-3 py-1.5 border border-white/15"
            >
              Silicone Version
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.22em] uppercase text-black bg-accent px-3 py-1.5" data-testid="available-today-label">
            Available Today
          </span>
          <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mt-5">
            WearSpray · Safe Band Pro
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight mt-4 leading-tight">
            What you need, right where you need it.
          </h2>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6">
            Safe Band Pro is a patented, refillable wrist-worn dispensing
            platform that keeps a chosen formulation immediately accessible.
          </p>
          <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4">
            Today&rsquo;s silicone and adjustable formats prove the core function,
            but the platform is built to flex. Face, band, materials, finish, and
            proportions can all be developed around a specific brand, formula, or
            occasion, from sport and active lifestyles to skincare, travel,
            luxury, and evening wear, turning the wrist into an immediate
            point of access for protection, personal care, and beauty.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-8">
            {FEATURES.map((f) => (
              <div key={f} className="flex items-start gap-3">
                <Check size={18} className="text-accent mt-0.5 shrink-0" strokeWidth={2} />
                <span className="font-body text-sm text-zinc-300">{f}</span>
              </div>
            ))}
          </div>

          <p className="font-body text-sm text-zinc-500 mt-8">
            Built on WearSpray&rsquo;s{" "}
            <Link to="/patents" className="text-accent hover:underline" data-testid="available-today-patent-link">issued U.S. patent portfolio</Link>.
          </p>

          <a
            href={SAFE_BAND_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="buy-safebandpro-cta"
            className="group inline-flex items-center gap-2 font-body text-sm font-medium bg-[#00e5ff] text-black px-8 py-4 mt-10 hover:bg-[#00b3cc] transition-colors"
          >
            Shop at SafeBandPro.com
            <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
        </Reveal>
      </section>

      {/* TWO CURRENT WEARABLE FORMATS */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="products-formats">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-4">One Core</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl">
              One dispensing core. <span className="text-accent">Different ways to wear it.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px mt-12 bg-zinc-800 border border-zinc-800">
            {FORMATS.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.08}>
                <div className="bg-[#121212] p-8 md:p-10 h-full" data-testid={`products-format-${i}`}>
                  <span className="font-mono text-xs tracking-[0.18em] uppercase text-accent">{f.label}</span>
                  <div className="flex flex-wrap gap-2.5 mt-5">
                    {f.tags.map((t) => (
                      <span key={t} className="font-body text-sm text-zinc-300 border border-zinc-700 px-3 py-1.5">{t}</span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FULL-WIDTH USAGE ANIMATION, edge to edge, full clip incl. surfers */}
      <section className="relative w-full bg-black border-y border-zinc-800" data-testid="products-fullwidth-banner">
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
          <video
            src="/prototype/safeband-full.mp4"
            poster="/prototype/safeband-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            data-testid="products-safeband-video"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-x-0 bottom-0 p-8 md:p-16 bg-gradient-to-t from-black/75 via-black/25 to-transparent pointer-events-none">
            <span className="font-mono text-xs md:text-sm tracking-[0.28em] uppercase text-accent block mb-2">
              Action Sports
            </span>
            <h3 className="font-heading text-3xl md:text-5xl font-light tracking-tight text-white" data-testid="velcro-version-headline">
              Velcro Version
            </h3>
          </div>
        </div>
      </section>

      {/* HOW TO FILL, demo video */}
      <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="products-fill-demo">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-4">
              How It Works
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight leading-tight max-w-3xl">
              Fill it. Wear it. Dispense it.
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-2xl">
              A refillable reservoir keeps a chosen formulation immediately
              accessible. Fill the reservoir, secure WearSpray to the wrist, and
              press to dispense directly into the palm or appropriate application
              area.
            </p>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4 max-w-2xl">
              Working alongside manufacturers and materials consultants, we are
              often asked how it works. It can be a hard concept to picture: a
              liquid, lotion, or even a gel held inside a wrist wearable. We built
              this demonstration so our production teams can see it clearly. Liquid,
              lotion, gel, they all work.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <FillDemoVideo />
          </Reveal>
        </div>
      </section>

      {/* ONE PLATFORM · MANY APPLICATIONS */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="products-applications">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-4">One Platform</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl">
              One platform. <span className="text-accent">Many applications.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-3xl">
              As we&rsquo;ve been selling and talking with our clients, we&rsquo;ve
              recognized over time that people have very different needs,
              and they&rsquo;ve been reaching for the band for all kinds of
              reasons. The same refillable platform quietly adapts to each of
              them.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px mt-14 bg-zinc-800 border border-zinc-800">
            {APPLICATIONS.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.t} delay={(i % 3) * 0.06}>
                  <div className="bg-[#121212] p-8 h-full hover:bg-[#161616] transition-colors" data-testid={`products-application-${i}`}>
                    <Icon size={24} strokeWidth={1.5} className="text-accent mb-5" />
                    <h3 className="font-heading text-xl text-white tracking-tight">{a.t}</h3>
                    <p className="font-body text-zinc-400 text-sm leading-relaxed mt-3">{a.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="font-body text-xs text-zinc-600 leading-relaxed mt-8 max-w-2xl">
            WearSpray is a dispensing platform. It does not treat medical
            conditions; any OTC or therapeutic use depends on the formulation
            selected and should follow that product&rsquo;s directions.
          </p>
        </div>
      </section>

      {/* WEARSPRAY VS BOTTLES COMPARISON */}
      <section className="border-t border-zinc-800" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent mb-5 block">
              Why Wear It
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl">
              Protection works better when it is within reach.
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-6 max-w-2xl">
              Personal care and protective products only work when they are
              available at the moment they are needed. Here is how WearSpray
              compares with conventional bottles, tubes, and sprays.
            </p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-14 border border-zinc-800 overflow-hidden">
              {/* Header row */}
              <div className="grid grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_200px_200px] bg-[#0d0d0d] border-b border-zinc-800">
                <div className="p-5 md:p-6" />
                <div className="p-4 md:p-6 text-center border-l border-zinc-800 bg-[#00e5ff]/[0.06]">
                  <span className="font-heading text-base md:text-xl font-medium text-[#00e5ff]">
                    WearSpray
                  </span>
                  <span className="hidden md:block font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500 mt-1">
                    Safe Band Pro
                  </span>
                </div>
                <div className="p-4 md:p-6 text-center border-l border-zinc-800">
                  <span className="font-heading text-base md:text-xl font-light text-zinc-400">
                    Bottles &amp; Sprays
                  </span>
                  <span className="hidden md:block font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-600 mt-1">
                    The Old Way
                  </span>
                </div>
              </div>

              {COMPARISON.map((row, i) => (
                <div
                  key={row}
                  data-testid={`comparison-row-${i}`}
                  className={`grid grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_200px_200px] items-center ${
                    i % 2 === 1 ? "bg-[#0d0d0d]" : "bg-transparent"
                  } ${i !== COMPARISON.length - 1 ? "border-b border-zinc-800" : ""}`}
                >
                  <div className="p-5 md:p-6 font-body text-sm md:text-base text-zinc-200 leading-snug">
                    {row}
                  </div>
                  <div className="p-5 md:p-6 flex justify-center border-l border-zinc-800 bg-[#00e5ff]/[0.04] h-full items-center">
                    <span className="w-8 h-8 rounded-full bg-[#00e5ff] text-black flex items-center justify-center">
                      <Check size={18} strokeWidth={2.5} />
                    </span>
                  </div>
                  <div className="p-5 md:p-6 flex justify-center border-l border-zinc-800 h-full items-center">
                    <span className="w-8 h-8 rounded-full border border-zinc-700 text-zinc-600 flex items-center justify-center">
                      <Minus size={18} strokeWidth={2.5} />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FROM PRODUCT TO PLATFORM */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="products-transition">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-28 md:py-36 text-center">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-6">From Product to Platform</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-[1.1]">
              The dispenser is only <span className="text-accent">the beginning.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-2xl mx-auto">
              WearSpray began with a simple objective: make personal care and
              protection instantly accessible. The same dispensing architecture
              can now extend into sensing, connected devices, and new form factors.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 mt-14" data-testid="products-progression">
              {PROGRESSION.map((s, i) => (
                <div key={s} className="flex items-center gap-3 md:gap-4">
                  <span className="font-mono text-sm tracking-[0.2em] uppercase text-white border border-zinc-700 px-4 py-2.5">{s}</span>
                  {i < PROGRESSION.length - 1 && <ChevronRight size={16} className="text-accent" />}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* FUTURE INTEGRATION · THE CONNECTED CASE */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="products-future-integration">
        <ConnectedCaseBanner />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent mb-5 block">
              Platform Extension
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-4xl">
              Personal care, built into{" "}
              <span className="text-accent">something you already carry.</span>
            </h2>
            <span className="inline-block font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-400 border border-zinc-700 px-3 py-1.5 mt-6" data-testid="products-mobile-concept-label">Platform Concept</span>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mt-14">
            <Reveal>
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/6cp5ijn4_cellphone%20dominos.jpg"
                alt="Concept render of magenta smartphone cases with a built-in refillable WearSpray reservoir and a companion app showing a personal care dashboard"
                loading="lazy"
                className="w-full h-auto object-contain"
                data-testid="future-integration-image"
              />
            </Reveal>

            <Reveal delay={0.1}>
              <div className="space-y-6 max-w-xl">
                <p className="font-body text-zinc-300 text-lg leading-relaxed">
                  This concept extends WearSpray&rsquo;s patented dispensing
                  platform into the phone itself. Built into a dedicated case or
                  added as a universal attachable module, a refillable reservoir
                  keeps sunscreen, moisturizer, serum, or fragrance immediately
                  within reach.
                </p>
                <p className="font-heading text-xl md:text-2xl font-light tracking-tight leading-snug text-white">
                  A simple rotation unlocks the dispensing position, and a
                  dedicated press area delivers product on demand. No bottle to
                  find. No cap to open. No disconnect between awareness and action.
                </p>
                <p className="font-body text-zinc-400 text-lg leading-relaxed">
                  The greater opportunity is the intelligence around it. You tell
                  the app what you loaded, and it tailors guidance to that formula
                 , UV-based reapplication for sunscreen, routines for
                  moisturizer, a well-timed prompt for fragrance, using
                  signals the phone already has, like location, time outdoors, and
                  conditions. Physical product and digital experience begin to work
                  as one system.
                </p>
                <p className="font-body text-zinc-300 text-lg leading-relaxed">
                  The value is context: protection is more effective when the
                  product is close at hand and the reminder arrives at the right
                  moment.
                </p>
              </div>
            </Reveal>
          </div>

          {/* PATENT LANGUAGE FOR MOBILE, small proof point */}
          <Reveal delay={0.1}>
            <div className="mt-16 border-l-2 border-accent pl-6 max-w-3xl" data-testid="products-mobile-patent">
              <span className="font-mono text-xs tracking-[0.22em] uppercase text-accent block mb-3">Described Within the WearSpray Patent Family</span>
              <p className="font-body text-zinc-300 leading-relaxed">
                The WearSpray patent disclosure includes device-attached
                embodiments, including a reservoir attached to the back of a cell
                phone, as well as connected computing, software, location, and
                wireless communication concepts.
              </p>
              <Link to="/patents" className="inline-flex items-center gap-1.5 font-mono text-xs tracking-[0.15em] uppercase text-accent mt-4 hover:underline" data-testid="products-mobile-patent-link">
                Explore the Patent Portfolio <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>

          {/* HOW IT WORKS, horizontal sequence */}
          <div className="mt-20 pt-16 border-t border-zinc-800">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-4">
                How It Works
              </span>
              <p className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug text-white max-w-3xl">
                A simple flow turns the phone into a personalized care platform.
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="flex flex-col lg:flex-row lg:items-stretch gap-3 lg:gap-2 mt-12" data-testid="products-mobile-flow">
                {MOBILE_STEPS.map((s, i) => (
                  <div key={s.t} className="flex flex-col lg:flex-row lg:items-stretch gap-3 lg:gap-2 lg:flex-1">
                    <div className="border border-zinc-800 bg-[#111] p-5 h-full lg:flex-1" data-testid={`products-mobile-step-${i}`}>
                      <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">0{i + 1}</span>
                      <h4 className="font-heading text-lg text-white mt-2 tracking-tight">{s.t}</h4>
                      <p className="font-body text-zinc-400 text-xs leading-relaxed mt-2">{s.d}</p>
                    </div>
                    {i < MOBILE_STEPS.length - 1 && (
                      <div className="flex lg:flex-col items-center justify-center text-accent shrink-0">
                        <ChevronRight size={16} className="hidden lg:block" />
                        <ChevronRight size={16} className="lg:hidden rotate-90" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          {/* Interactive app mockup, options change by user-selected formula */}
          <div className="mt-20 pt-16 border-t border-zinc-800">
            <Reveal>
              <span className="font-mono text-xs tracking-[0.24em] uppercase text-accent block mb-4">Connected Experience</span>
              <h3 className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-snug text-white max-w-3xl">
                The formula changes. <span className="text-accent">The experience changes with it.</span>
              </h3>
              <p className="font-body text-zinc-400 text-lg leading-relaxed mt-5 max-w-2xl">
                You identify what is loaded in the reservoir, and the app provides
                relevant context, guidance, reminders, and routines based on that
                formulation and the information the phone already has. The
                reservoir does not electronically detect its own contents,
                you select the formulation.
              </p>
            </Reveal>
            <Reveal>
              <AppMockup />
            </Reveal>
          </div>

          {/* Ergonomics, natural hand position + gravity assist */}
          <div className="mt-20 pt-16 border-t border-zinc-800">
            <Reveal>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-testid="connected-case-ergonomics">
                <div className="relative">
                  <div
                    className="pointer-events-none absolute -inset-6 blur-3xl opacity-40"
                    style={{ background: "radial-gradient(circle, #ff2d7855, transparent 70%)" }}
                  />
                  <img loading="lazy"
                    src="/prototype/ergonomics-dark.jpg"
                    alt="A hand pressing the dispense button on the back of a WearSpray phone reservoir"
                    className="relative w-full h-auto rounded-2xl border border-zinc-800"
                    data-testid="ergonomics-image"
                  />
                </div>
                <div>
                  <span className="font-mono text-xs tracking-[0.24em] uppercase text-accent block mb-4">
                    Ergonomics by Design
                  </span>
                  <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight leading-snug text-white max-w-md">
                    A natural place for the hand. A natural place for the button.
                  </h3>
                  <div className="mt-8 space-y-7 max-w-md">
                    <div className="flex gap-4">
                      <ShieldCheck className="w-5 h-5 text-accent shrink-0 mt-1" />
                      <div>
                        <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-400 block mb-1.5">
                          Natural Reach
                        </span>
                        <p className="font-body text-zinc-300 leading-relaxed">
                          The dispense button sits exactly where a finger naturally
                          falls when the phone is held, a comfortable, secure
                          grip that makes a single press feel effortless in one hand.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <RefreshCw className="w-5 h-5 text-accent shrink-0 mt-1" />
                      <div>
                        <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-400 block mb-1.5">
                          Gravity-Assisted
                        </span>
                        <p className="font-body text-zinc-300 leading-relaxed">
                          Turning the phone face-down as you press lets gravity guide
                          the solution into the dispensing position, the same
                          principle that makes the wearable work.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>


        </div>
      </section>


      {/* SENSE » UNDERSTAND » PROMPT » ACT » LEARN, cinematic lead-in banner */}
      <SenseRelayBanner idPrefix="products" />

      {/* SENSE → UNDERSTAND → PROMPT → ACT → LEARN flow */}
      <section className="border-t border-zinc-800 bg-black" data-testid="products-sense-act">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-5">The WearSpray Loop</span>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-[1.1] max-w-3xl">
              From capturing data to helping you act,{" "}
              <span className="text-accent">and learning as it goes.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <SenseActFlow />
          </Reveal>
          <p className="font-heading text-xl md:text-2xl font-light tracking-tight text-white mt-10">
            Sense. Understand. Prompt. Act. <span style={{ color: "#b794f6" }}>Learn.</span>
          </p>
        </div>
      </section>

      {/* FROM SENSING TO RELEVANCE, the relevance engine */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="products-relevance">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent">From Sensing to Relevance</span>
              <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-zinc-400 border border-zinc-700 px-2.5 py-1">Near-Term Concept · In Development</span>
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-[1.15] max-w-4xl">
              The sensor creates awareness. The phone creates context.{" "}
              <span className="text-accent">WearSpray creates access and application.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-8 space-y-5 max-w-3xl">
              <p className="font-body text-zinc-300 text-lg leading-relaxed">
                As the sensor and app come together, WearSpray can combine body
                sensing, environmental intelligence, and application history to
                recognize when topical care may become relevant.
              </p>
              <p className="font-body text-zinc-400 leading-relaxed">
                The sensor does not need to diagnose a condition or automatically
                dispense a product. It provides physiological context. The
                smartphone adds environmental context. WearSpray connects that
                intelligence to a topical solution already being carried on the
                wrist.
              </p>
            </div>
          </Reveal>

          {/* Three converging streams */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px mt-16 bg-zinc-800 border border-zinc-800" data-testid="relevance-streams">
            {[
              { icon: Signal, t: "Body Sensing", items: ["Heart Rate", "Movement + Activity", "Body Temperature", "Sweat", "Blood Oxygen", "Other Physiological Signals"] },
              { icon: MapPin, t: "Environmental Context", items: ["UV Index", "GPS + Location", "Temperature", "Humidity", "Weather Conditions", "Time + Duration"] },
              { icon: Clock, t: "Application Context", items: ["What is being carried", "When it was last applied", "Time since application", "Activity since application", "User preferences + routines"] },
            ].map((col, i) => {
              const Icon = col.icon;
              return (
                <Reveal key={col.t} delay={i * 0.08}>
                  <div className="bg-[#111] p-8 h-full" data-testid={`relevance-stream-${i}`}>
                    <div className="flex items-center gap-3 mb-6">
                      <Icon size={18} className="text-accent" />
                      <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">{col.t}</span>
                    </div>
                    <ul className="space-y-2.5">
                      {col.items.map((it) => (
                        <li key={it} className="font-body text-sm text-zinc-300 flex items-center gap-3">
                          <span className="w-1.5 h-1.5 bg-[#00e5ff] shrink-0" />
                          {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Converge into relevance engine */}
          <Reveal delay={0.1}>
            <div className="flex flex-col items-center mt-8" data-testid="relevance-engine">
              <ChevronRight size={20} className="text-accent rotate-90" />
              <div className="relative border border-accent/40 bg-[#0c0c0c] px-10 py-6 mt-4 overflow-hidden text-center">
                <div className="absolute -inset-8 blur-3xl opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.22), transparent 65%)" }} />
                <span className="relative font-heading text-xl md:text-2xl font-light tracking-tight text-white">Relevance Engine</span>
              </div>
              <RelevanceFlow />
            </div>
          </Reveal>

          {/* Three application cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20" data-testid="relevance-cards">
            {[
              { icon: Droplets, t: "Moisturizer", sub: "When the environment changes, topical care can respond.", body: "Low humidity, temperature, body or skin temperature, activity, and time since the previous application can provide context associated with increased moisture loss or a potential need to refresh moisturizer. WearSpray does not need to diagnose dry skin, it can recognize conditions in which moisturizing may become more relevant and bring that awareness to the user.", prompt: "Dry environmental conditions and extended exposure detected. Consider refreshing your moisturizer.", signals: "Humidity • Temperature • Body/Skin Temperature • Activity • Application History" },
              { icon: Sun, t: "Sunscreen", sub: "Protection informed by exposure, not simply a timer.", body: "UV conditions, location, heat, outdoor activity, sweat, and elapsed time since the previous application can work together to create a more relevant sunscreen reapplication prompt. Instead of relying solely on a fixed timer, WearSpray can understand the circumstances surrounding the user and recognize when protection may deserve renewed attention.", prompt: "High UV and sustained outdoor activity detected. Consider reapplying protection.", signals: "UV • GPS • Temperature • Activity • Sweat • Time Since Application" },
              { icon: Bug, t: "Mosquito Repellent", sub: "Protection informed by where you are and what is happening around you.", body: "Location, time of day, season, environmental conditions, outdoor activity, and application history can help identify circumstances when insect protection may become more relevant. The system can alert the user as exposure conditions change, while the repellent itself remains immediately accessible on the wrist.", prompt: "Conditions associated with increased insect activity detected. Consider applying protection.", signals: "GPS • Time • Season • Environment • Outdoor Activity • Application History" },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.t} delay={i * 0.08}>
                  <div className="border border-zinc-800 bg-[#111] p-7 h-full flex flex-col" data-testid={`relevance-card-${i}`}>
                    <Icon size={24} strokeWidth={1.5} className="text-accent mb-5" />
                    <h3 className="font-heading text-2xl font-light tracking-tight text-white">{c.t}</h3>
                    <p className="font-body text-sm text-accent leading-snug mt-2">{c.sub}</p>
                    <p className="font-body text-sm text-zinc-400 leading-relaxed mt-5">{c.body}</p>
                    <div className="border-l-2 border-accent bg-[#0c0c0c] px-4 py-3 mt-6">
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 flex items-center gap-2 mb-2"><Bell size={11} className="text-accent" /> Example Prompt</span>
                      <p className="font-body text-sm text-zinc-200 italic leading-snug">&ldquo;{c.prompt}&rdquo;</p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-zinc-800">
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500 block mb-2">Signals</span>
                      <p className="font-mono text-[11px] text-zinc-400 leading-relaxed">{c.signals}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* One architecture, multiple forms of action */}
          <Reveal delay={0.1}>
            <div className="mt-24 border-t border-zinc-800 pt-16">
              <h3 className="font-heading text-2xl md:text-4xl font-light tracking-tight leading-[1.12] max-w-4xl">
                One sensor architecture.{" "}
                <span className="text-accent">Multiple forms of action.</span>
              </h3>
              <div className="mt-6 space-y-4 max-w-3xl">
                <p className="font-body text-zinc-400 leading-relaxed">
                  The sensor does not need to know what is inside the WearSpray
                  reservoir. The user identifies the topical solution within the
                  WearSpray app, and the same sensing architecture can then
                  interpret biometric, environmental, and contextual information
                  differently according to what the user is carrying.
                </p>
                <p className="font-body text-zinc-400 leading-relaxed">
                  Sunscreen creates one relevance model. Moisturizer creates
                  another. Mosquito repellent creates another. Personal care,
                  protection, and future topical applications can create others,
                  letting a single wearable sensing and dispensing architecture
                  support multiple forms of contextual, immediate topical care.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Existing sensors, new utility */}
          <Reveal delay={0.1}>
            <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 border border-zinc-800 bg-[#0d0d0d] p-8 md:p-12">
              <div className="lg:col-span-7">
                <span className="font-mono text-xs tracking-[0.22em] uppercase text-accent block mb-4">Existing Sensors · New Utility</span>
                <p className="font-body text-zinc-300 text-lg leading-relaxed">
                  WearSpray does not need to reinvent biometric sensing.
                  Commercially available screenless sensor technology can already
                  provide physiological signals such as activity, movement, heart
                  rate, and temperature, while the smartphone provides GPS, UV,
                  weather, humidity, time, and additional environmental context.
                </p>
              </div>
              <div className="lg:col-span-5 lg:border-l lg:border-zinc-800 lg:pl-10 flex flex-col justify-center">
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">WearSpray introduces the missing physical layer</span>
                <p className="font-heading text-2xl md:text-3xl font-light tracking-tight text-accent leading-snug">Immediate access to action.</p>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mt-4">
                  The intelligence does not end with a measurement or notification.
                  The topical solution is already being carried on the wearable and
                  can be immediately accessed and applied by the user.
                </p>
              </div>
            </div>
          </Reveal>

          {/* From personal care to topical health, near term / future */}
          <Reveal delay={0.1}>
            <div className="mt-20">
              <span className="font-mono text-xs tracking-[0.22em] uppercase text-accent block mb-4">From Personal Care to Topical Health</span>
              <p className="font-body text-zinc-400 leading-relaxed max-w-3xl">
                The same architecture creates a pathway for future exploration of
                appropriate topical health and over-the-counter applications. Many
                topical products are already delivered as liquids, lotions, creams,
                gels, and sprays, creating an opportunity to explore how
                sensing, contextual prompting, and immediate wearable access could
                support additional user-controlled topical applications.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
                <div className="border border-accent/30 bg-[#101010] p-8" data-testid="relevance-nearterm">
                  <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent block mb-5">Near Term</span>
                  <ul className="space-y-3">
                    {["Contextual sensing", "Intelligent prompting", "User-controlled dispensing", "Immediate topical application"].map((t) => (
                      <li key={t} className="font-body text-sm text-zinc-300 flex items-center gap-3"><span className="w-1.5 h-1.5 bg-[#00e5ff] shrink-0" />{t}</li>
                    ))}
                  </ul>
                </div>
                <div className="border border-zinc-800 bg-[#101010] p-8" data-testid="relevance-future">
                  <span className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 block mb-5">Future</span>
                  <ul className="space-y-3">
                    {["Additional skin and environmental sensing", "More sophisticated application intelligence", "Appropriate OTC topical applications", "Controlled or metered therapeutic delivery where required"].map((t) => (
                      <li key={t} className="font-body text-sm text-zinc-400 flex items-center gap-3"><span className="w-1.5 h-1.5 bg-zinc-600 shrink-0" />{t}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Closing statement */}
          <Reveal delay={0.1}>
            <div className="mt-24 text-center max-w-4xl mx-auto" data-testid="relevance-closing">
              <h3 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.12]">
                Most wearables tell you something.{" "}
                <span className="text-accent">WearSpray gives you something to do about it.</span>
              </h3>
              <p className="font-heading text-xl md:text-2xl font-light tracking-tight text-white mt-8">
                Sense. Contextualize. Prompt. <span className="text-accent">Apply.</span>
              </p>
              <p className="font-body text-zinc-400 text-lg leading-relaxed mt-10 max-w-3xl mx-auto">
                Not everyone wants a wearable on their wrist just to reapply.
                Some people would rather keep the smartwatch they already love,
                and still have a way to act in the moment. So we bring the
                biometrics into our app and offer the reservoir as a delivery
                system on the back of the phone instead.
              </p>
              <p className="font-body text-zinc-400 text-lg leading-relaxed mt-4 max-w-3xl mx-auto">
                The same rules apply, you designate what is loaded in the
                reservoir, and future sensing coordination can be integrated over
                time. We see a quick path toward a simple, refillable delivery
                system built right into the back of the phone.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTION 11 · PLATFORM PROGRESSION */}
      <section className="border-t border-zinc-800 bg-[#0a0a0a]" data-testid="products-roadmap">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl">
              One architecture. <span className="text-accent">An expanding platform.</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px mt-14 bg-zinc-800 border border-zinc-800">
            {ROADMAP.map((r, i) => (
              <Reveal key={r.n} delay={i * 0.06}>
                <div className="bg-[#121212] p-8 h-full" data-testid={`products-roadmap-${i}`}>
                  <span className="font-heading text-4xl font-light text-accent">{r.n}</span>
                  <h3 className="font-heading text-lg text-white mt-4 tracking-tight">{r.t}</h3>
                  <p className="font-body text-zinc-400 text-sm leading-relaxed mt-3">{r.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/vision" className="inline-flex items-center gap-1.5 font-mono text-xs tracking-[0.15em] uppercase text-accent mt-10 hover:underline" data-testid="products-roadmap-vision-link">
              Explore the Vision <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTION 12 · FINAL CTA */}
      <section className="border-t border-zinc-800 bg-black" data-testid="products-final-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-28 md:py-36 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.12]">
              The next wearable category may not just measure more.{" "}
              <span className="text-accent">It may do more.</span>
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-8 max-w-2xl mx-auto">
              WearSpray is building an expandable platform around one simple idea:
              when information tells you that something matters, the solution
              should already be within reach.
            </p>
            <div className="flex flex-wrap gap-4 justify-center mt-12">
              <Link to="/contact" data-testid="products-cta-partner" className="font-body text-sm font-medium text-black bg-accent px-8 py-4 hover:-translate-y-0.5 transition-transform">Partner With WearSpray</Link>
              <Link to="/patents" data-testid="products-cta-patents" className="font-body text-sm font-medium text-white border border-zinc-700 px-8 py-4 hover:border-accent hover:text-accent transition-colors">Explore the Patents</Link>
            </div>
          </Reveal>
        </div>
      </section>


    </div>
  );
}
